import React from 'react'
import { Head, Link } from '@inertiajs/react'
import classNames from 'classnames/bind'
import AdminLayout from '@/admin/shell/layouts/AdminLayout'
import '../../../styles/admin.scss'
import styles from '../Error.module.scss'

const cx = classNames.bind(styles)

const ERROR_COPY: Record<number, { title: string; sub: string; code: string }> = {
  403: {
    title: "Access denied",
    sub: "You don't have permission to view this page. Contact an administrator if you believe this is a mistake.",
    code: 'FORBIDDEN',
  },
  404: {
    title: "This page wandered off",
    sub: "The link may be outdated, or the page might have been moved or deleted. Try the dashboard to get back on track.",
    code: 'NOT_FOUND',
  },
  500: {
    title: "Something went wrong",
    sub: "An unexpected error occurred on the server. Please try again in a moment, or contact support if the problem persists.",
    code: 'INTERNAL_SERVER_ERROR',
  },
}

interface ErrorPageProps {
  status: number
}

function ErrorPage({ status }: ErrorPageProps) {
  const isForbidden = status === 403
  const copy = ERROR_COPY[status] ?? {
    title: "Unexpected error",
    sub: "An unknown error occurred.",
    code: 'UNKNOWN',
  }

  return (
    <div
      data-admin-root={isForbidden ? '' : undefined}
      data-theme={isForbidden ? 'light' : undefined}
      className={isForbidden ? cx('errorRoot') : undefined}
    >
      <Head title={`Admin — ${status}`} />
      <div className={cx('wrap')}>
        <div className={cx('card')}>
          <span className={cx('eyebrow')}>Error · {copy.code.replace(/_/g, ' ').toLowerCase()}</span>
          <div className={cx('code')}>{status}</div>
          <h1 className={cx('title')}>{copy.title}</h1>
          <p className={cx('sub')}>{copy.sub}</p>

          <div className={cx('actions')}>
            <Link href={isForbidden ? '/app' : '/admin'} className={cx('btn', 'btnPrimary')}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 12 12 3l9 9" />
                <path d="M5 10v10h14V10" />
              </svg>
              {isForbidden ? 'Go to app' : 'Back to dashboard'}
            </Link>
            <a href="javascript:history.back()" className={cx('btn', 'btnGhost')}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Go back
            </a>
          </div>

          <div className={cx('meta')}>
            <span><strong>STATUS</strong> {status}</span>
            <span><strong>CODE</strong> {copy.code}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

ErrorPage.layout = (page: React.ReactNode) => {
  const status = React.isValidElement<ErrorPageProps>(page) ? page.props.status : undefined

  return status === 403 ? page : <AdminLayout title="Error">{page}</AdminLayout>
}

export default ErrorPage
